import type { MessageDescriptor } from "@lingui/core";
import type { Setter } from "solid-js";
import { useCommandHandler } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { useUndo } from "@/lib/undo";
import type { useTheme } from "@/providers/theme-provider";
import type { NavigateFn, useLocation } from "@tanstack/solid-router";

interface AppShortcutsOptions {
  location: ReturnType<typeof useLocation>;
  navigate: NavigateFn;
  theme: ReturnType<typeof useTheme>;
  t: (message: MessageDescriptor) => string;
  setShortcutsOpen: Setter<boolean>;
  setIsSidebarOpen: Setter<boolean>;
}

export function useAppShortcuts(options: AppShortcutsOptions) {
  // ── Shortcut handlers (declarative; bindings live in lib/shortcuts/defaults.ts)
  // ── Global undo (L1): Ctrl/Cmd+Z undoes the session's last action ────────
  const { undoLast } = useUndo();
  useCommandHandler(COMMAND_IDS.UNDO, () => {
    void undoLast();
  });

  useCommandHandler(COMMAND_IDS.HELP_OPEN, () => {
    options.setShortcutsOpen(true);
  });

  // ── UI toggles ─────────────────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_SIDEBAR, () => {
    options.setIsSidebarOpen((prev) => !prev);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BLUR, () => {
    void options.theme.toggleBlur();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, () => {
    void options.theme.toggleFollowDevice();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE, () => {
    options.theme.toggleBackgroundStyle();
  });

  // ── Navigation to generic infra routes ───────────────────────────────────
  useCommandHandler(COMMAND_IDS.GOTO_DASHBOARD, () => {
    void options.navigate({ to: "/" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_PROFILE, () => {
    void options.navigate({ to: "/profile" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_THEMES, () => {
    void options.navigate({ to: "/themes" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_SHORTCUTS, () => {
    void options.navigate({ to: "/profile/shortcuts" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_LANGUAGE, () => {
    void options.navigate({ to: "/language" });
  });
}
